import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const strong = pw.length >= 8 && /[a-z]/.test(pw) && /[A-Z]/.test(pw) && /\d/.test(pw) && /[^A-Za-z0-9]/.test(pw);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Les mots de passe ne correspondent pas");
    if (!strong) return toast.error("Mot de passe trop faible");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      toast.success("Mot de passe réinitialisé");
      setTimeout(() => nav("/login"), 1800);
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F6FB] p-6">
      <div className="w-full max-w-md card p-8">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#16324F] to-[#2563EB] flex items-center justify-center mb-4"><KeyRound className="w-6 h-6 text-white" /></div>
        {done ? (
          <div className="text-center py-4" data-testid="reset-done">
            <CheckCircle2 className="w-12 h-12 text-[#15803D] mx-auto mb-3" />
            <h2 className="text-xl font-bold font-head text-[#16324F]">Mot de passe mis à jour</h2>
            <p className="text-sm text-[#64748B] mt-1">Redirection vers la connexion…</p>
          </div>
        ) : !token ? (
          <div className="text-center py-4">
            <h2 className="text-xl font-bold font-head text-[#16324F]">Lien invalide</h2>
            <p className="text-sm text-[#64748B] mt-1">Ce lien de réinitialisation est incomplet ou expiré.</p>
            <Link to="/login" className="btn-outline mt-4 inline-flex" data-testid="reset-back-login">Retour à la connexion</Link>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-bold font-head text-[#16324F]">Nouveau mot de passe</h2>
            <p className="text-sm text-[#64748B] mt-1 mb-6">Choisissez un mot de passe robuste pour sécuriser votre compte.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="lbl">Nouveau mot de passe</label>
                <div className="relative">
                  <input type={showPw ? "text" : "password"} className="inp !pr-11" data-testid="reset-password-input" value={pw} onChange={(e) => setPw(e.target.value)} required />
                  <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]" data-testid="reset-password-toggle" aria-label={showPw ? "Masquer" : "Afficher"}>{showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                </div>
                <p className={`text-xs mt-1 ${strong ? "text-[#15803D]" : "text-[#94A3B8]"}`}>Min. 8 caractères, avec majuscule, minuscule, chiffre et caractère spécial.</p>
              </div>
              <div>
                <label className="lbl">Confirmer le mot de passe</label>
                <div className="relative">
                  <input type={showPw2 ? "text" : "password"} className="inp !pr-11" data-testid="reset-password-confirm" value={pw2} onChange={(e) => setPw2(e.target.value)} required />
                  <button type="button" onClick={() => setShowPw2((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]" data-testid="reset-password-confirm-toggle" aria-label={showPw2 ? "Masquer" : "Afficher"}>{showPw2 ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="reset-submit-btn">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Réinitialiser le mot de passe
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
